import type { SupabaseClient } from '@supabase/supabase-js';

import {
  captureServiceError,
  captureServiceWarning,
} from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';
import type { Database } from '@kit/supabase/database';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * One row returned by `get_public_featured_events(p_user_id)`.
 *
 * Mirrors the RPC's RETURNS TABLE definition. `production_*` and
 * `cast_role_*` are nullable because not every event is tied to a
 * production / role (e.g. ad-hoc gigs).
 */
export interface PublicFeaturedEvent {
  assignmentId: string;
  eventId: string;
  eventTitle: string;
  eventDate: string;
  eventLocation: string | null;
  productionId: string | null;
  productionName: string | null;
  castRoleId: string | null;
  castRoleName: string | null;
  castNumber: number | null;
  /** External ticketing link (Ticketmaster / Eventbrite / company shop).
   * Surfaced as a "Get tickets" CTA on upcoming-event tiles. Added in
   * migration 20260512100000 (events.ticket_url). Null when no ticket
   * link is set. */
  ticketUrl: string | null;
}

/**
 * Public Featured Events service.
 *
 * Why an RPC, not a `from('cast_assignments').select()`:
 * `cast_assignments` RLS is locked to owner + client members. Loosening
 * it for one read use case would carry six months of casting-permission
 * context. Instead, the SECURITY DEFINER RPC
 * `public.get_public_featured_events` does the public-safe filtering
 * (accepted past assignments, dancer hasn't opted out, public-safe
 * columns only) and is granted to `anon` + `authenticated`. See
 * `apps/web/supabase/migrations/20260510150000_get_public_featured_events_rpc.sql`.
 */
export function createFeaturedEventsService(client: SupabaseClient<Database>) {
  return {
    /**
     * Fetch a dancer's accepted cast assignments for public display.
     * Defaults to past credentials only (legacy behaviour). Pass
     * `includeFuture: true` to also surface upcoming bookings — used
     * by the redesigned profile to render an "Upcoming performances"
     * group above the past credits. Capped at 24 rows by the RPC.
     * Empty array on success with no rows; `Result.fail` only on a
     * real RPC error.
     */
    async getPublicFeaturedEvents(
      userId: string | null | undefined,
      options: { includeFuture?: boolean } = {},
    ): Promise<Result<PublicFeaturedEvent[]>> {
      // Guard: legacy Meteor-migrated dancer profiles can have a NULL
      // `user_id` (no Supabase auth user was ever provisioned). Callers
      // sometimes coerce that to `""` to satisfy non-null types, which
      // then crashes the RPC with `invalid input syntax for type uuid:
      // ""` at Postgres parse time. There's nothing to fetch for a
      // user-less profile — return an empty success.
      if (!userId || !UUID_REGEX.test(userId)) {
        return Result.ok([]);
      }

      try {
        const { data, error } = await client.rpc('get_public_featured_events', {
          p_user_id: userId,
          p_include_future: options.includeFuture ?? false,
        });

        if (error) {
          // Transient gateway errors (Cloudflare 522, Supabase overload) return
          // an HTML page instead of JSON. The Supabase client surfaces the raw
          // HTML as `error.message`. Reporting this to Sentry as an error
          // produces noise without actionability — treat it as a warning and
          // return an empty list so the dancer profile page still renders.
          if (isGatewayError(error.message)) {
            captureServiceWarning(
              {
                message: 'getPublicFeaturedEvents: transient gateway error',
                code: 'GATEWAY_ERROR',
                details: { rawMessage: error.message.slice(0, 200) },
              },
              { action: 'getPublicFeaturedEvents', userId },
            );
            return Result.ok([]);
          }
          captureServiceError(
            { message: error.message, code: 'RPC_ERROR' },
            { action: 'getPublicFeaturedEvents', userId },
          );
          return Result.fail(
            new ServiceError(
              'FETCH_ERROR',
              `Failed to fetch featured events: ${error.message}`,
              error,
            ),
          );
        }

        const rows = (data ?? []).map(
          (row): PublicFeaturedEvent => ({
            assignmentId: row.assignment_id,
            eventId: row.event_id,
            eventTitle: row.event_title,
            eventDate: row.event_date,
            eventLocation: row.event_location,
            productionId: row.production_id,
            productionName: row.production_name,
            castRoleId: row.cast_role_id,
            castRoleName: row.cast_role_name,
            castNumber: row.cast_number,
            ticketUrl: row.ticket_url,
          }),
        );

        return Result.ok(rows);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        // Same gateway-error guard for fetch-level failures (e.g. the client
        // itself throws because the response body wasn't valid JSON).
        if (isGatewayError(message)) {
          captureServiceWarning(
            {
              message: 'getPublicFeaturedEvents: transient fetch error',
              code: 'GATEWAY_ERROR',
              details: { rawMessage: message.slice(0, 200) },
            },
            { action: 'getPublicFeaturedEvents', userId },
          );
          return Result.ok([]);
        }
        captureServiceError(
          { message, code: 'FETCH_ERROR' },
          { action: 'getPublicFeaturedEvents', userId },
        );
        return Result.fail(
          new ServiceError(
            'FETCH_ERROR',
            `Failed to fetch featured events: ${message}`,
            err,
          ),
        );
      }
    },
  };
}

export type FeaturedEventsService = ReturnType<
  typeof createFeaturedEventsService
>;

/**
 * Returns true when `message` looks like an HTML gateway error page rather
 * than a real PostgREST/application error.
 *
 * Cloudflare 522 (connection timeout) and similar CDN/load-balancer errors
 * return an HTML response that the Supabase client surfaces as the raw
 * response body in `error.message`.  These are transient infrastructure
 * events — there is no code fix, and filing them as Sentry errors produces
 * unactionable noise.
 */
function isGatewayError(message: string): boolean {
  return (
    message.trimStart().startsWith('<!DOCTYPE') ||
    message.trimStart().startsWith('<html') ||
    message.includes('522') ||
    message.toLowerCase().includes('cloudflare') ||
    // Supabase edge function cold-start HTML responses
    message.includes('Service Unavailable') ||
    message.includes('Bad Gateway')
  );
}
